import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, TrendingUp, ExternalLink } from "lucide-react";
import { getBackendUrl, authHeaders } from "../../lib/http";

type Confidence = "low" | "medium" | "high";

interface SalesEstimate {
  ready: boolean;
  windowDays: number;
  from: string | null;
  to: string | null;
  shop: {
    snapshotCount: number;
    latestTotalSales: number | null;
    totalSales: number;
    avgPerDay: number;
    coveredDays: number;
    daily: { date: string; sales: number; interpolated: boolean }[];
  };
  totalFavGain: number;
  confidence: Confidence;
  listings: {
    id: string;
    title: string;
    url: string;
    imageUrl: string | null;
    price: string | null;
    favoriteCount: number;
    favGain: number;
    sharePct: number;
    estimatedSales: number;
    snapshotCount: number;
    confidence: Confidence;
  }[];
  notes: string[];
}

const WINDOWS = [7, 30, 90] as const;

const CONF_LABEL: Record<Confidence, { text: string; cls: string }> = {
  low: { text: "Düşük", cls: "bg-amber-50 text-amber-700" },
  medium: { text: "Orta", cls: "bg-gray-100 text-gray-600" },
  high: { text: "Yüksek", cls: "bg-emerald-50 text-emerald-700" },
};

async function fetchEstimate(shopId: string, window: number): Promise<SalesEstimate> {
  const res = await fetch(`${getBackendUrl()}/api/shops/${shopId}/sales-estimate?window=${window}`, {
    headers: authHeaders(),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.message || `Hata ${res.status}`);
  return body.data as SalesEstimate;
}

function formatDay(iso: string) {
  const [, m, d] = iso.split("-").map(Number);
  const months = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  return `${d} ${months[m - 1]}`;
}

function DailyBars({ daily }: { daily: SalesEstimate["shop"]["daily"] }) {
  const max = Math.max(1, ...daily.map((d) => d.sales));
  const w = 100 / daily.length;
  return (
    <div>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-28 w-full" role="img" aria-label="Günlük satış">
        {daily.map((d, i) => {
          const h = (d.sales / max) * 38;
          return (
            <rect
              key={d.date}
              x={i * w + w * 0.15}
              y={40 - h}
              width={w * 0.7}
              height={Math.max(h, d.sales > 0 ? 0.6 : 0)}
              rx={0.4}
              className={d.interpolated ? "fill-indigo-300" : "fill-indigo-600"}
            >
              <title>{`${formatDay(d.date)}: ${d.sales} satış${d.interpolated ? " (aradaki günlere bölündü)" : ""}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-gray-400">
        <span>{formatDay(daily[0].date)}</span>
        <span>en yüksek {max.toLocaleString("tr-TR")}/gün</span>
        <span>{formatDay(daily[daily.length - 1].date)}</span>
      </div>
    </div>
  );
}

export default function SalesEstimateSection({ shopId }: { shopId: string }) {
  const [win, setWin] = useState<number>(30);
  const { data, isLoading, error } = useQuery({
    queryKey: ["shop-sales-estimate", shopId, win],
    queryFn: () => fetchEstimate(shopId, win),
    enabled: !!shopId,
  });

  return (
    <div className="mb-8 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <TrendingUp className="h-4 w-4 text-indigo-600" />
          Satış Tahmini
        </h3>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setWin(w)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                win === w ? "bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {w} gün
            </button>
          ))}
        </div>
      </div>
      <p className="mb-4 text-xs text-gray-500">
        Mağaza toplamları gerçek (Etsy transaction_sold_count günlük farkı); ürün dağılımı aynı dönemdeki favori artışına göre tahmindir.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
          Yükleniyor…
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{(error as Error).message}</div>
      ) : !data || !data.ready ? (
        <div className="flex flex-col items-center py-8 text-center">
          <TrendingUp className="mb-2 h-8 w-8 text-gray-300" />
          <p className="text-sm text-gray-500">Veri birikiyor — en az 2 günlük senkron gerekir.</p>
          {data && data.shop.snapshotCount > 0 && (
            <p className="mt-1 text-xs text-gray-400">Şu an {data.shop.snapshotCount} günlük kayıt var.</p>
          )}
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Dönem satışı</p>
              <p className="text-xl font-semibold text-gray-900 tabular-nums">{data.shop.totalSales.toLocaleString("tr-TR")}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Günlük ort.</p>
              <p className="text-xl font-semibold text-gray-900 tabular-nums">{data.shop.avgPerDay.toLocaleString("tr-TR")}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Kapsanan gün</p>
              <p className="text-xl font-semibold text-gray-900 tabular-nums">{data.shop.coveredDays}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Güven</p>
              <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${CONF_LABEL[data.confidence].cls}`}>
                {CONF_LABEL[data.confidence].text}
              </span>
            </div>
          </div>

          {data.shop.daily.length > 0 && <DailyBars daily={data.shop.daily} />}

          {data.notes.length > 0 && (
            <ul className="mt-3 space-y-0.5 text-xs text-gray-500">
              {data.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}

          {data.listings.length > 0 && (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Ürün</th>
                    <th className="px-3 py-2 text-right">Favori artışı</th>
                    <th className="px-3 py-2 text-right">Pay</th>
                    <th className="px-3 py-2 text-right">Tahmini satış</th>
                    <th className="px-3 py-2">Güven</th>
                  </tr>
                </thead>
                <tbody>
                  {data.listings.map((l) => (
                    <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <a
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center gap-2"
                        >
                          {l.imageUrl ? (
                            <img src={l.imageUrl} alt="" className="h-9 w-9 flex-shrink-0 rounded-lg object-cover" />
                          ) : (
                            <div className="h-9 w-9 flex-shrink-0 rounded-lg bg-gray-100" />
                          )}
                          <span className="line-clamp-1 text-gray-700 group-hover:text-indigo-700">{l.title}</span>
                          <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 group-hover:text-indigo-500" />
                        </a>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">+{l.favGain}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">%{l.sharePct.toLocaleString("tr-TR")}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">
                        ~{l.estimatedSales.toLocaleString("tr-TR")}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${CONF_LABEL[l.confidence].cls}`}>
                          {CONF_LABEL[l.confidence].text}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
